import { px } from '@adsk/alloy-react-helpers';

import { stateOf } from '../helpers';
import {
  AlloyLegacyThemeDesignTokens,
  ButtonDesignTokens,
  Weave3ButtonInput,
} from '../types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = WeaveTheme['tokens'];

const fontFamily = 'Artifakt Element, sans-serif';

/** Fallback small button size values matching the legacy alloy theme */
const SMALL_BUTTON_FALLBACK = {
  height: '24px',
  'font-size': '12px',
  'line-height': '16px',
  'padding-text': '0 7px',
  'padding-icon': '0 3px',
  'padding-icon-text': '0 7px 0 5px',
  'icon-size': 16,
} as const;

export const getButtonTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): ButtonDesignTokens => ({
  'border-radius': '2px',
  'border-width': '1px',
  'font-family': theme.typography.labelMedium.fontFamily,
  'font-weight': theme.typography.labelMedium.fontWeight,
  solid: {
    'background-color': theme.tokens.colors.background.brand.value,
    'border-color': 'transparent',
    'text-color': theme.tokens.colors.text.inverse.value,
    icon: { fill: theme.tokens.colors.icon.inverse.value },
    hover: {
      'background-color': theme.tokens.colors.background.brand.hover.value,
    },
    pressed: {
      'background-color': theme.tokens.colors.background.brand.pressed.value,
    },
    selected: {
      'background-color': theme.tokens.colors.background.brand.pressed.value,
    },
    disabled: {
      'background-color': theme.tokens.colors.background.neutral.inactive.value,
      'text-color': theme.tokens.colors.text.inactive.value,
      icon: { fill: theme.tokens.colors.icon.inactive.value },
    },
    loading: {
      'background-color': theme.tokens.colors.background.brand.inactive.value,
    },
    alert: {
      'background-color': theme.tokens.colors.background.error.value,
      'text-color': theme.tokens.colors.text.error.inverse.value,
      pressed: {
        'background-color': theme.tokens.colors.background.error.bold.value,
      },
      loading: {
        'background-color': theme.tokens.colors.background.error.inactive.value,
      },
      selected: {
        'background-color': theme.tokens.colors.background.error.selected.value,
      },
    },
  },
  outlined: {
    'border-color': theme.tokens.colors.border.value,
    'text-color': theme.tokens.colors.text.primary.value,
    icon: { fill: theme.tokens.colors.icon.secondary.value },
    hover: {
      icon: { fill: theme.tokens.colors.icon.hover.value },
    },
    pressed: {
      'border-color': theme.tokens.colors.border.pressed.value,
      icon: { fill: theme.tokens.colors.icon.pressed.value },
    },
    focus: {
      icon: { fill: theme.tokens.colors.icon.pressed.value },
    },
    active: {
      icon: { fill: theme.tokens.colors.icon.selected.value },
    },
    selected: {
      'background-color': theme.tokens.colors.background.neutral.selected.value,
    },
    disabled: {
      'border-color': theme.tokens.colors.border.inactive.value,
      'text-color': theme.tokens.colors.text.inactive.value,
      icon: { fill: theme.tokens.colors.icon.inactive.value },
    },
    loading: {
      'border-color': theme.tokens.colors.border.inactive.value,
      'text-color': theme.tokens.colors.text.inactive.value,
    },
    alert: {
      'text-color': theme.tokens.colors.text.error.value,
      icon: { fill: theme.tokens.colors.icon.error.bold.value },
    },
  },
  flat: {
    'border-color': 'transparent',
    'text-color': theme.tokens.colors.text.link.value,
    icon: { fill: theme.tokens.colors.icon.link.value },
    hover: {
      'background-color': theme.tokens.colors.background.neutral.hover.value,
      'text-color': theme.tokens.colors.text.hover.value,
      icon: { fill: theme.tokens.colors.icon.hover.value },
    },
    pressed: {
      'background-color': theme.tokens.colors.background.neutral.pressed.value,
      'text-color': theme.tokens.colors.text.pressed.value,
      icon: { fill: theme.tokens.colors.icon.pressed.value },
    },
    selected: {
      'background-color': theme.tokens.colors.background.neutral.selected.value,
    },
    disabled: {
      'text-color': theme.tokens.colors.text.inactive.value,
      icon: { fill: theme.tokens.colors.icon.inactive.value },
    },
    loading: {
      'text-color': theme.tokens.colors.text.inactive.value,
    },
    alert: {
      'text-color': theme.tokens.colors.text.error.value,
      icon: { fill: theme.tokens.colors.icon.error.bold.value },
    },
  },
  small: {
    height: '24px',
    'font-size': theme.typography.labelSmall.fontSize,
    'line-height': theme.typography.labelSmall.lineHeight,
    'padding-text': '0 7px',
    'padding-icon': '0 3px',
    'padding-icon-text': '0 7px 0 5px',
    'icon-size': 16,
  },
  medium: {
    height: '36px',
    'font-size': theme.typography.labelMedium.fontSize,
    'line-height': theme.typography.labelMedium.lineHeight,
    'padding-text': '0 15px',
    'padding-icon': '0 5px',
    'padding-icon-text': '0 15px 0 11px',
    'icon-size': 24,
  },
  'box-shadow': {
    focus: theme.halos.focus,
    hover: theme.halos.hover,
    pressed: theme.halos.active,
  },
  'icon-margin': px(theme.spacing.XS),
  link: {
    small: theme.typography.bodySmall,
    medium: theme.typography.bodyMedium,
  },
});

/**
 * @param tokens - The base Weave design tokens
 * @param highDensityTokens - high-density tokens used to populate the small button size
 */
export const getButtonTokensWeave = (
  tokens: WeaveTokens,
  highDensityTokens?: WeaveTokens,
): ButtonDesignTokens => {
  const button = tokens?.component?.button;
  const semantic = tokens?.semantic;
  const generic = tokens?.generic;
  const highButton = highDensityTokens?.component?.button;
  const highGeneric = highDensityTokens?.generic;

  const solidBg = button?.solid?.['background-color'];
  const solidText = button?.solid?.['text-color'];
  const solidIcon = button?.solid?.icon?.fill;
  const errorBg = semantic?.['status-color']?.error;

  const solid = {
    ...button?.solid,
    'background-color': solidBg,
    'border-color': button?.solid?.['border-color'],
    'text-color': solidText,
    icon: { fill: solidIcon },
    hover: { 'background-color': solidBg },
    pressed: { 'background-color': solidBg },
    selected: { 'background-color': solidBg },
    disabled: {
      'background-color': solidBg,
      'text-color': solidText,
      icon: { fill: solidIcon },
    },
    loading: { 'background-color': solidBg },
    alert: {
      'background-color': errorBg,
      'text-color': solidText,
      hover: { 'background-color': errorBg },
      pressed: { 'background-color': errorBg },
      loading: { 'background-color': errorBg },
      selected: { 'background-color': errorBg },
    },
  };

  const outlinedBorderColor = button?.outlined?.['border-color'] as
    | { default?: string; pressed?: string }
    | string
    | undefined;
  const outlinedBorderDefault =
    typeof outlinedBorderColor === 'object'
      ? outlinedBorderColor?.default
      : outlinedBorderColor;
  const outlinedBorderPressed =
    typeof outlinedBorderColor === 'object'
      ? outlinedBorderColor?.pressed
      : undefined;

  const outlined = {
    ...button?.outlined,
    'border-color': outlinedBorderDefault,
    'text-color': button?.outlined?.['text-color'],
    icon: {
      fill: button?.outlined?.icon?.fill,
    },
    hover: {
      icon: { fill: semantic?.['icon-color']?.control?.hover },
    },
    pressed: {
      'border-color': outlinedBorderPressed,
      icon: {
        fill: semantic?.['icon-color']?.control?.pressed,
      },
    },
    focus: {
      icon: {
        fill: semantic?.['icon-color']?.control?.pressed,
      },
    },
    active: {
      icon: { fill: semantic?.['icon-color']?.control?.active },
    },
    selected: {
      'background-color': semantic?.['background-color']?.selected?.default,
      'border-color': semantic?.['border-color']?.selected,
    },
    disabled: {
      'border-color': outlinedBorderDefault,
      'text-color': button?.outlined?.['text-color'],
      icon: {
        fill: button?.outlined?.icon?.fill,
      },
    },
    loading: {
      'border-color': outlinedBorderDefault,
      'text-color': button?.outlined?.['text-color'],
    },
    alert: {
      'text-color': semantic?.['status-color']?.error,
      icon: { fill: semantic?.['status-color']?.error },
    },
  };

  const flat = {
    ...button?.flat,
    'border-color': button?.flat?.['border-color'],
    'text-color': button?.flat?.['text-color'],
    icon: {
      fill: button?.flat?.icon?.fill,
    },
    hover: {
      'background-color': 'transparent',
      'text-color': semantic?.['text-color']?.medium?.default,
      icon: {
        fill: semantic?.['icon-color']?.control?.hover,
      },
    },
    pressed: {
      'background-color': 'transparent',
      'text-color': semantic?.['text-color']?.medium?.default,
    },
    selected: {
      'background-color': button?.segmented?.['background-color']?.pressed,
    },
    disabled: {
      'text-color': button?.flat?.['text-color'],
      icon: {
        fill: button?.flat?.icon?.fill,
      },
    },
    loading: {
      'text-color': button?.flat?.['text-color'],
    },
    alert: {
      'text-color': semantic?.['status-color']?.error,
      icon: {
        fill: semantic?.['status-color']?.error,
      },
    },
  };

  const buttonSize = {
    height: button?.height,
    'font-size': button?.['font-size'],
    'line-height': button?.['line-height'],
    'padding-text': `0 ${generic?.spacing?.s}`,
    'padding-icon': `0 ${generic?.spacing?.xs}`,
    'padding-icon-text': `0 ${generic?.spacing?.s} 0 ${generic?.spacing?.xs}`,
    'icon-size': Number.parseInt(button?.icon?.width || '16', 10),
  };

  const smallButtonSize = highDensityTokens
    ? {
        height: highButton?.height,
        'font-size': highButton?.['font-size'],
        'line-height': highButton?.['line-height'],
        'padding-text': `0 ${highGeneric?.spacing?.s}`,
        'padding-icon': `0 ${highGeneric?.spacing?.xs}`,
        'padding-icon-text': `0 ${highGeneric?.spacing?.s} 0 ${highGeneric?.spacing?.xs}`,
        'icon-size': Number.parseInt(highButton?.icon?.width || '16', 10),
      }
    : SMALL_BUTTON_FALLBACK;

  const buildBoxShadow = (state: 'focus' | 'hover' | 'pressed') =>
    `${button?.['box-shadow-x']?.[state]}px ${button?.['box-shadow-y']?.[state]}px ${button?.['box-shadow-blur']?.[state]}px ${button?.['box-shadow-spread']?.[state]}px ${button?.['box-shadow-color']?.[state]}`;

  return {
    ...button,
    'font-family': fontFamily,
    'border-radius': button?.['border-radius'],
    'border-width': button?.['border-width'],
    'font-weight': button?.['font-weight'],
    solid,
    outlined,
    flat,
    small: smallButtonSize,
    medium: buttonSize,
    'box-shadow': {
      focus: buildBoxShadow('focus'),
      hover: buildBoxShadow('hover'),
      pressed: buildBoxShadow('pressed'),
    },
    'icon-margin':
      button?.['icon-left']?.['icon-container']?.['margin-right'] ??
      generic?.spacing?.xs,
    opacity: {
      disabled: button?.opacity?.disabled,
    },
    link: {
      small: {
        fontSize: highDensityTokens
          ? highButton?.['font-size']
          : button?.['font-size'],
        lineHeight: highDensityTokens
          ? highButton?.['line-height']
          : button?.['line-height'],
        fontFamily,
      },
      medium: {
        fontSize: button?.['font-size'],
        lineHeight: button?.['line-height'],
        fontFamily,
      },
    },
  };
};

const padding = (node?: {
  'padding-top'?: string;
  'padding-right'?: string;
  'padding-bottom'?: string;
  'padding-left'?: string;
}): string | undefined => {
  const top = node?.['padding-top'];
  const right = node?.['padding-right'];
  const bottom = node?.['padding-bottom'];
  const left = node?.['padding-left'];
  if (!top || !right || !bottom || !left) {
    return undefined;
  }
  return top === bottom && left === right
    ? `${top} ${left}`
    : `${top} ${right} ${bottom} ${left}`;
};

export const getButtonTokensWeave3 = (
  button?: Weave3ButtonInput,
): ButtonDesignTokens => {
  const solid = button?.solid;
  const solidBg = solid?.['background-color'];
  const solidErrorBg = solid?.error?.['background-color']?.default;
  const outlined = button?.outlined;
  const outlinedBg = outlined?.['background-color'];
  const outlinedBorder = outlined?.['border-color'];
  const outlinedError = outlined?.error;
  const flat = button?.flat;
  const flatBg = flat?.['background-color'];
  const flatError = flat?.error;

  const shadow = (state: 'focus' | 'hover' | 'pressed'): string | undefined => {
    const color = button?.['box-shadow-color']?.[state];
    const spread = button?.['box-shadow-spread']?.[state] ?? '0px';
    return color ? `0 0 0 ${spread} ${color}` : undefined;
  };

  /**
   * The W3 token tree carries a single per-edge padding set per size node, with
   * no text-vs-icon-vs-icon+text distinction (unlike Alloy/W2, where the three
   * slots differ). So all three `padding-*` slots resolve to the same shorthand
   * — faithful to the source. If design later wants tighter icon-only padding,
   * that's a token-source change, not a factory one.
   */
  const size = (node?: Weave3ButtonInput | Weave3ButtonInput['small']) => ({
    height: node?.height,
    'font-size': node?.['font-size'],
    'line-height': node?.['line-height'],
    'padding-text': padding(node),
    'padding-icon': padding(node),
    'padding-icon-text': padding(node),
    'icon-size': Number.parseInt(button?.icon?.size?.width ?? '16', 10),
  });

  const disabledOpacity = button?.opacity?.disabled;

  return {
    'border-radius': button?.['border-radius'],
    'border-width': button?.['border-width'],
    'font-family': fontFamily,
    'font-weight': button?.['font-weight'],
    solid: {
      'background-color': solidBg?.default,
      'border-color': stateOf(solid?.['border-color'], 'default'),
      'text-color': stateOf(solid?.['text-color'], 'default'),
      icon: { fill: stateOf(solid?.icon?.fill, 'default') },
      hover: { 'background-color': solidBg?.hover },
      pressed: { 'background-color': solidBg?.pressed },
      selected: {
        'background-color': solidBg?.selected,
        'border-color': stateOf(solid?.['border-color'], 'selected'),
        'text-color': stateOf(solid?.['text-color'], 'selected'),
        icon: { fill: stateOf(solid?.icon?.fill, 'selected') },
      },
      disabled: {
        'background-color': solidBg?.default,
        'text-color': stateOf(solid?.['text-color'], 'default'),
        icon: { fill: stateOf(solid?.icon?.fill, 'default') },
      },
      loading: { 'background-color': solidBg?.progress },
      alert: {
        'background-color': solidErrorBg,
        'text-color': stateOf(solid?.['text-color'], 'default'),
        icon: { fill: stateOf(solid?.icon?.fill, 'default') },
        hover: { 'background-color': solidErrorBg },
        pressed: { 'background-color': solidErrorBg },
        loading: { 'background-color': solidErrorBg },
        selected: { 'background-color': solidErrorBg },
      },
    },
    outlined: {
      'background-color': outlinedBg?.default,
      'border-color': outlinedBorder?.default,
      'text-color': stateOf(outlined?.['text-color'], 'default'),
      icon: { fill: stateOf(outlined?.icon?.fill, 'default') },
      hover: { 'background-color': outlinedBg?.hover },
      pressed: {
        'background-color': outlinedBg?.pressed,
        'border-color': outlinedBorder?.pressed,
      },
      focus: { 'border-color': outlinedBorder?.focus },
      selected: {
        'background-color': outlinedBg?.selected,
        'border-color': outlinedBorder?.selected,
      },
      disabled: {
        'border-color': outlinedBorder?.default,
        'text-color': stateOf(outlined?.['text-color'], 'default'),
        icon: { fill: stateOf(outlined?.icon?.fill, 'default') },
      },
      loading: {
        'border-color': outlinedBorder?.default,
        'text-color': stateOf(outlined?.['text-color'], 'default'),
      },
      alert: {
        'text-color': stateOf(outlinedError?.['text-color'], 'default'),
        'border-color': outlinedError?.['border-color']?.selected,
        icon: { fill: stateOf(outlinedError?.icon?.fill, 'default') },
      },
    },
    flat: {
      'background-color': flatBg?.default,
      'text-color': stateOf(flat?.['text-color'], 'default'),
      icon: { fill: stateOf(flat?.icon?.fill, 'default') },
      hover: { 'background-color': flatBg?.hover },
      pressed: { 'background-color': flatBg?.pressed },
      selected: {
        'background-color': flatBg?.selected,
        'border-color': flat?.['border-color']?.selected,
      },
      disabled: {
        'text-color': stateOf(flat?.['text-color'], 'default'),
        icon: { fill: stateOf(flat?.icon?.fill, 'default') },
      },
      loading: { 'text-color': stateOf(flat?.['text-color'], 'default') },
      alert: {
        'text-color': stateOf(flatError?.['text-color'], 'default'),
        icon: { fill: stateOf(flatError?.icon?.fill, 'default') },
      },
    },
    medium: size(button),
    small: size(button?.small),
    'box-shadow': {
      focus: shadow('focus'),
      hover: shadow('hover'),
      pressed: shadow('pressed'),
    },
    'icon-margin': button?.gap,
    opacity: {
      disabled: disabledOpacity
        ? String(Number.parseFloat(disabledOpacity) / 100)
        : undefined,
    },
    link: {
      small: {
        fontSize: button?.small?.['font-size'],
        lineHeight: button?.small?.['line-height'],
        fontFamily,
      },
      medium: {
        fontSize: button?.['font-size'],
        lineHeight: button?.['line-height'],
        fontFamily,
      },
    },
  };
};
